<template>
  <div class="page-title" v-hotkey="keymap">
    <div class="title">{{title}}</div>
    <div class="date-time" v-if="!disableDateTime">
      <el-tooltip placement="bottom-start" content="Today (T)" :open-delay="500">
        <el-button class="today-button"  plain @click="updateCurrentDateRangeIndex(0)" :disabled="currentRangeIndex.index === 0"><font-awesome-icon icon='calendar-day'/></el-button>
      </el-tooltip>
      <el-button icon="el-icon-arrow-left" class="time-nav previous" @click="decrementDateRangeIndex" :disabled="currentRangeIndex.range === 'custom'"></el-button>
      <el-select :value="currentRangeString" placeholder="Select" @change='updateCurrentDateRangeRange' >
        <el-option v-for="item in options" :key="item.value" :label="item.label" :value="item.value" :disabled="weekOnly">
        </el-option>
      </el-select>
      <el-button  icon="el-icon-arrow-right" class="time-nav next" @click="incrementDateRangeIndex" :disabled="currentRangeIndex.range === 'custom'"></el-button>
      <el-date-picker
        class="date-range-picker"
        v-model="currentDateRangeSelection"
        type="daterange"
        align="right"
        unlink-panels
        range-separator="|"
        start-placeholder="Start date"
        end-placeholder="End date"
        format="yy-MM-dd"
        :clearable="false"
        :picker-options="pickerOptions">  
      </el-date-picker>
      <el-button class="submit-button" :type="selectedEntriesSubmitted ? 'warning' : 'primary'" plain @click="handleSubmitClick(!selectedEntriesSubmitted)" v-if="!$store.getters.adminMode">
        <span v-if="selectedEntriesSubmitted">Unsubmit</span>
        <span v-if="!selectedEntriesSubmitted">Submit</span>
      </el-button>
      <el-button class="lock-button" :type="selectedEntriesLocked ? 'warning' : 'primary'" plain @click="lock(!selectedEntriesLocked)" v-if="$store.getters.adminMode">
        <span v-if="selectedEntriesLocked">Unlock</span>
        <span v-if="!selectedEntriesLocked">Lock</span>
      </el-button>
      <div>
        <div><font-awesome-icon icon="check"/> {{submittedEntries.length}} / {{selectedEntries.length}}</div>
        <div v-if="$store.getters.adminMode"><font-awesome-icon icon="lock"/> {{lockedEntries.length}} / {{selectedEntries.length}}</div>
      </div>
    </div>
    <el-popover class="info" placement="top-start" title="Versions" width="180" trigger="hover">
      <div>
        <div class="el-col-12">App:</div>
        <div class="el-col-12">{{versions.appVersion}}</div>
      </div>
      <div>
        <div class="el-col-12">API:</div>
        <div class="el-col-12">{{versions.apiVersion}}</div>
      </div>
      <div>
        <div class="el-col-12">Server:</div>
        <div class="el-col-12">{{versions.serverVersion}}</div>
      </div>
      <div>
        <div class="el-col-12">Timezone:</div>
        <div class="el-col-12">{{$store.state.authentication.user.attributes.timezone}}</div>
      </div>
      <div slot="reference">
        <font-awesome-icon icon="info" class="info-button-icon"/>
      </div>
    </el-popover>
  </div>
</template>

<script>
import { EventHub } from '@/components/EventHub'
import { getDateRangeFromRangeIndex, getRangeIndexFromDateRange } from '@/utils/timeRange'
import { setTimeEntrySubmitted, setTimeEntryLocked } from '@/utils/timeEntry'
import moment from 'moment'

export default {
  props: {
    title: String,
    disableDateTime: Boolean,
    weekOnly: Boolean
  },
  data () {
    return {
      currentDate: moment(),
      versions: this.$store.state.versions.versions,
      pickerOptions: {
        firstDayOfWeek: this.$store.getters.firstDayOfWeek
      },
      options: [
        {
          label: 'Month (M)',
          value: 'month'
        },
        {
          label: 'Week (W)',
          value: 'week'
        },
        {
          label: 'Day (D)',
          value: 'day'
        },
        {
          label: 'Custom',
          value: 'custom',
          disabled: true
        }
      ]
    }
  },
  methods: {
    handleD (e) {
      if (e.target.getAttribute('type') !== 'text') {
        this.updateCurrentDateRangeRange('day')
      }
    },
    handleW (e) {
      if (e.target.getAttribute('type') !== 'text') {
        this.updateCurrentDateRangeRange('week')
      }
    },
    handleM (e) {
      if (e.target.getAttribute('type') !== 'text') {
        this.updateCurrentDateRangeRange('month')
      }
    },
    handleT (e) {
      if (e.target.getAttribute('type') !== 'text') {
        this.updateCurrentDateRangeIndex(0)
      }
    },
    handleLeft (e) {
      if (e.target.getAttribute('type') !== 'text') {
        this.decrementDateRangeIndex()
      }
    },
    handleRight (e) {
      if (e.target.getAttribute('type') !== 'text') {
        this.incrementDateRangeIndex()
      }
    },
    updateCurrentDateRangeRange (input) {
      let index = this.currentRangeIndex.index
      if (!this.currentRangeIndex.index) {
        index = 0
      }
      this.$store.commit('updateCurrentDateRange', getDateRangeFromRangeIndex(input, index))

      if (input === 'custom') {
        this.$store.commit('applyCustomDateRange')
      }
    },
    decrementDateRangeIndex () {
      this.updateCurrentDateRangeIndex(this.currentRangeIndex.index - 1)
    },
    incrementDateRangeIndex () {
      this.updateCurrentDateRangeIndex(this.currentRangeIndex.index + 1)
    },
    updateCurrentDateRangeIndex (input) {
      this.$store.commit('updateCurrentDateRange', getDateRangeFromRangeIndex(this.currentRangeIndex.range, input))
    },
    handleSubmitClick (state) {
      if (state) {
        this.auditTimeEntries(this.selectedEntries)
      } else {
        this.submit(state)
      }
    },
    auditTimeEntries (entries) {
      let errors = {
        shortEntries: [],
        overlappingEntries: [],
        nullTaskEntries: []
      }

      let warnings = {
        longEntries: [],
        nonDefaultActivityEntries: []
      }
      if (this.selectedEntries.length !== 0) {
        this.selectedEntries.forEach(entry => {
          if (entry.duration > 6) {
            warnings.longEntries.push(entry)
          }
          if (entry.duration < 0.1) {
            errors.shortEntries.push(entry)
          }
          if (entry.overlapsStart || entry.overlapsEnd) {
            errors.overlappingEntries.push(entry)
          }
          if (!entry.task) {
            errors.nullTaskEntries.push(entry)
          }
          if (entry.activity !== entry.defaultActivity) {
            warnings.nonDefaultActivityEntries.push(entry)
          }
        })

        if (warnings.longEntries.length > 0 || errors.overlappingEntries.length > 0 || errors.nullTaskEntries.length > 0 || errors.shortEntries.length > 0 || warnings.nonDefaultActivityEntries.length > 0) {
          EventHub.$emit('show-entry-audit-dialog', {errors, warnings, onApprove: () => { this.submit(true) }})
        } else {
          this.submit(true)
        }
      }
    },
    submit (state) {
      let ending = this.selectedEntries.length > 1 ? 'ies' : 'y'
      let status = state ? '' : 'un'
      this.$confirm('You are about to ' + status + 'submit ' + this.selectedEntries.length + ' time entr' + ending + '.\n\n Do you wish to continue?').then(() => { // And they confirm they want to submit
        let promises = []
        this.$store.commit('setAppIsLoading', true)
        promises.push(setTimeEntrySubmitted(this.selectedEntries, state))
        Promise.all(promises).then(() => {
          this.$store.dispatch('time-entries/loadAll', this.$store.getters.timeEntryFilters).then(() => {
            this.$store.commit('setAppIsLoading', false)
            this.$message({
              showClose: true,
              message: 'All entries ' + status + 'submitted',
              type: 'success'
            })
          })
        })
      }).catch(_ => {})
    },
    lock (state) {
      if (this.selectedEntries.length !== 0) {
        let ending = this.selectedEntries.length > 1 ? 'ies' : 'y'
        let status = state ? '' : 'un'
        this.$confirm('You are about to ' + status + 'lock ' + this.selectedEntries.length + ' time entr' + ending + '.\n\n Do you wish to continue?').then(() => {
          let promises = []
          this.$store.commit('setAppIsLoading', true)
          promises.push(setTimeEntryLocked(this.selectedEntries, state))
          Promise.all(promises).then(() => {
            this.$store.dispatch('time-entries/loadAll', this.$store.getters.timeEntryFilters).then(() => {
              this.$store.commit('setAppIsLoading', false)
              this.$message({
                showClose: true,
                message: 'All entries ' + status + 'locked',
                type: 'success'
              })
            })
          })
        }).catch(_ => {})
      }
    }
  },
  computed: {
    keymap () {
      return {
        // bind to hotkey
        'd': (e) => { this.handleD(e) },
        'w': (e) => { this.handleW(e) },
        'm': (e) => { this.handleM(e) },
        't': (e) => { this.handleT(e) },
        'left': (e) => { this.handleLeft(e) },
        'right': (e) => { this.handleRight(e) }

      }
    },
    entries () {
      return this.$store.getters.filteredEntries.filter(entry => !entry.isPersonal)
    },
    selectedEntries () {
      let selectedEntries = this.entries.filter(entry => entry.selected)

      return selectedEntries.length > 0 ? selectedEntries : this.entries
    },
    selectedEntriesLocked () {
      let ret = true
      this.selectedEntries.forEach(entry => {
        if (!entry.attributes.isLocked) {
          ret = false
        }
      })
      return ret
    },
    selectedEntriesSubmitted () {
      let ret = true
      this.selectedEntries.forEach(entry => {
        if (!entry.attributes.isSubmitted) {
          ret = false
        }
      })
      return ret
    },
    unsubmittedEntries () {
      return this.selectedEntries.filter(entry => !entry.attributes.isSubmitted && !entry.isPersonal)
    },
    unlockedEntries () {
      return this.selectedEntries.filter(entry => !entry.attributes.isLocked && !entry.isPersonal)
    },
    submittedEntries () {
      return this.selectedEntries.filter(entry => entry.attributes.isSubmitted && !entry.isPersonal)
    },
    lockedEntries () {
      return this.selectedEntries.filter(entry => entry.attributes.isLocked && !entry.isPersonal)
    },
    currentRangeString () {
      let ret = 'Custom'
      let rangeIndex = this.currentRangeIndex
      if (rangeIndex.range === 'week' || rangeIndex.range === 'month') {
        if (rangeIndex.index === 0) {
          ret = 'This '
        } else if (rangeIndex.index === -1) {
          ret = 'Last '
        } else if (rangeIndex.index === 1) {
          ret = 'Next '
        }
      }
      if (rangeIndex.range === 'day') {
        if (rangeIndex.index === 0) {
          ret = 'To'
        } else if (rangeIndex.index === -1) {
          ret = 'Yester'
        } else if (rangeIndex.index === 1) {
          ret = 'Tomorrow'
        }
      }
      if (rangeIndex.index > 1) {
        ret = String(rangeIndex.index) + ' '
      } else if (rangeIndex.index < -1) {
        ret = String(rangeIndex.index * -1) + ' '
      }
      if (rangeIndex.range !== 'custom' && !(rangeIndex.range === 'day' && rangeIndex.index === 1)) {
        ret = ret + rangeIndex.range
        if (rangeIndex.index < -1) {
          ret = ret + 's ago'
        }
        if (rangeIndex.index > 1) {
          ret = ret + 's out'
        }
      }
      return ret
    },
    currentRangeIndex () {
      return getRangeIndexFromDateRange(this.$store.getters.currentDateRange, this.currentDate)
    },
    currentDateRangeSelection: {
      get: function () {
        return this.$store.getters.currentDateRange
      },
      set: function (data) {
        this.$store.commit('updateCurrentDateRange', data)
        if (this.currentRangeString === 'Custom') {
          this.$store.commit('setCustomDateRange')
        }
      }
    }
  },
  mounted () {
    if (this.weekOnly) {
      this.updateCurrentDateRangeRange('week')
    }

    window.setInterval(() => { this.currentDate = moment() }, 60000)
  }
}
</script>\

<style lang='scss'>
  .page-title {
    display: flex;
    margin: 15px 0px !important;

    .date-range-picker{
      width:180px !important;
    }

    .el-input__icon.el-range__close-icon{
      display:none !important;
      width:0px !important;
    }

    .title {
      font-size: 36px;
      font-weight: 100;
    }
    .date-time {
      display: flex;
      align-items: center;
      margin-left: 20px;

      .submit-button, .lock-button {
        margin-right: 5px;
      }
      .el-date-editor {
        width: 225px;
        margin: 0px 5px;
      }
      .el-select {
        width: 135px;

        input {
          border-radius: 0px;
          border-width: 1px 0px;
          border-color: rgb(220, 223,230) !important;
        }
      }
      .time-nav {
        width: 40px;
        padding: 12px 0px;
        background-color: #f5f7fa;

        &:focus {
          border-color: #dcdfe6;
          color: #606266;
        }
        &:hover {
          color: #409EFF;
          border-color: #c6e2ff;
          background-color: #ecf5ff;
        }
        &.previous {
          border-radius: 4px 0px 0px 4px;
        }
        &.next {
          border-radius: 0px 4px 4px 0px;
        }
        &.is-disabled {
          color: rgba(0,0,0,0.25);
          background: rgba(255,255,255,0.1);
          cursor: not-allowed;
          border-color: #dcdfe6;
        }
      }
    }
    .fa-check, .fa-lock {
      color: gray;
      font-size: 12px;
      margin-bottom: 2px;
    }
    .info {
      display: block;
      width: 20px;
      height: 20px;
      background: #fff;
      font-size: 11px;
      position: absolute;
      right: 20px;
      top: 25px;
      border-radius: 50%;
      border: 1px solid rgba(0,0,0,0.5);
      cursor: pointer;

      div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        margin-top: -1px;
      }
    }
  }
</style>